<template>
  <router-view
    :progress="progress"
    :handle-next-event="goToNextOrSubmit"
    @back="goToPreviousRoute"
  />
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ROUTE from '@shared/modules/assessment/router/names'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import useRenewalAssessmentStore from '@shared/modules/assessment/stores/useRenewalAssessmentStore'
import * as apiRequest from '@shared/api-endpoints'
import Tracker from '@shared/Tracker'

const route = useRoute()
const router = useRouter()
const assessmentStore = useAssessmentStore()
const renewalAssessmentStore = useRenewalAssessmentStore()

// The final question depends on whether the app handed us a nameable therapy
// goal: with one we ask how far it was achieved, without one we fall back to
// asking whether the therapy helped at all.
const steps = computed(() => [
  ROUTE.ASSESSMENT_RENEWAL_CHECK_IN,
  ROUTE.ASSESSMENT_RENEWAL_PGIC,
  ROUTE.ASSESSMENT_RENEWAL_PGIC_REFLECTION,
  ...(renewalAssessmentStore.hasGoal
    ? [
        ROUTE.ASSESSMENT_RENEWAL_GOAL_ACHIEVEMENT,
        ROUTE.ASSESSMENT_RENEWAL_GOAL_ACHIEVEMENT_REFLECTION,
      ]
    : [
        ROUTE.ASSESSMENT_RENEWAL_THERAPY_FEEDBACK,
        ROUTE.ASSESSMENT_RENEWAL_THERAPY_FEEDBACK_REFLECTION,
      ]),
])

const currentStepIndex = computed(() => steps.value.indexOf(route.name))

const progress = computed(() => {
  const index = Math.max(0, currentStepIndex.value)
  const range = assessmentStore.onboardingProgressRange
  if (range != null) {
    const { startProgress, endProgress } = range
    return (
      startProgress +
      (endProgress - startProgress) * ((index + 1) / (steps.value.length + 1))
    )
  }
  return (index + 1) / steps.value.length
})

const stepAt = (modifier) => {
  const index = currentStepIndex.value + modifier
  if (currentStepIndex.value < 0 || index < 0 || index >= steps.value.length) {
    return null
  }
  return steps.value[index]
}

// The tracking context takes flat key-value data, so send the answer keys rather
// than the nested request body.
const trackingPayload = ({ pgic, renewalTherapyFeedback }) => ({
  pgic_overall_change: pgic.overall_change.key,
  goal_achievement: renewalTherapyFeedback.goal_achievement?.key,
  referenced_goal_key:
    renewalTherapyFeedback.goal_achievement?.referenced_goal_key,
  therapy_helpfulness: renewalTherapyFeedback.therapy_helpfulness?.key,
})

// Submitting is awaited while the CTA stays enabled, so guard against the second
// tap that would submit and navigate twice.
const isSubmitting = ref(false)

// The last reflection is the end of the flow: submit both questionnaires, then
// hand over to the finish route the entry point registered (see the EU router
// hooks, which pick it from the corporate's insurance type).
const goToNextOrSubmit = async () => {
  const next = stepAt(+1)

  if (next != null) {
    await router.push({ name: next, query: route.query })
    return
  }

  if (isSubmitting.value) return
  isSubmitting.value = true

  try {
    const submission = renewalAssessmentStore.submission
    // Null when an answer is missing, which only a direct hit on this route can
    // produce — leave the flow without submitting a half-empty questionnaire.
    if (submission != null) {
      try {
        await apiRequest.submitRenewalAssessment(submission)
        Tracker.trackKaiaEvent(
          {
            event_name: 'c.lfcycl.submit_renewal_assessment',
            action: 'submit',
            object_type: 'renewal_assessment',
            screen_name: 'renewal_assessment_complete',
          },
          trackingPayload(submission),
        )
      } catch (error) {
        // A failed submission must not strand the user mid-renewal — the
        // questionnaire is supporting data, not a gate on renewing.
        console.warn(error)
      }
    }

    // The flow is over: drop the finish route the entry point registered, so a
    // later questionnaire in this session does not inherit it. SelfTest.vue
    // clears the same context on unmount.
    const { finishRoute } = assessmentStore
    assessmentStore.resetEmbeddedFlowContext()

    await router.push({
      ...finishRoute,
      query: route.query,
    })
  } finally {
    isSubmitting.value = false
  }
}

const goToPreviousRoute = () => {
  const previous = stepAt(-1)
  if (previous == null) return

  router.push({ name: previous, query: route.query })
}
</script>
